/**
 * navTracker — singleton that records the user's SPA route history so any
 * page can build a context-aware Back button without needing every <Link>
 * to pass `state={{ from }}`.
 *
 * Mounted via <NavTrackerProbe /> in Layout.jsx. On every React Router
 * location change it appends the new URL to the internal `stack`.
 * `getPreviousUrl()` returns the entry immediately below the top (or null
 * on first load).
 *
 * Backward navigation unwinds the stack: when `recordVisit` is called with
 * a URL that already appears earlier in the stack, every entry above that
 * position is dropped. This prevents the Back → Forward → Back ping-pong
 * that the old prev/current pair produced when the user unwound more than
 * one level (Item → Collection → Collections → Dashboard).
 *
 * Rotation (portrait ↔ landscape) does not change `location.pathname` or
 * `location.search`, so the `recordVisit` effect in Layout.jsx does not
 * fire. And even if a stray call arrives with the current top of the
 * stack, the `top === href` guard below no-ops it — so no duplicate
 * history entries ever land in the stack.
 *
 * Persisted to sessionStorage so a refresh on a deep link still surfaces
 * the previous URL (within the same browser tab). Legacy `{current, prev}`
 * snapshots are ported forward on read.
 */
const STORAGE_KEY = "cdi.nav.history";
const MAX_STACK = 32;

const state = {
  stack: [],
};

// Restore on module init so a refresh doesn't wipe context.
try {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  if (raw) {
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.stack)) {
      state.stack = parsed.stack.filter((s) => typeof s === "string" && s);
    } else if (parsed && typeof parsed.current === "string") {
      // Legacy shape: {current, prev}. Port forward so users mid-session
      // don't lose their breadcrumb after this fix ships.
      state.stack = [];
      if (typeof parsed.prev === "string" && parsed.prev) state.stack.push(parsed.prev);
      state.stack.push(parsed.current);
    }
  }
} catch { /* noop */ }

function persist() {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* noop */ }
}

export function recordVisit(href) {
  if (typeof href !== "string" || !href) return;
  const top = state.stack[state.stack.length - 1];
  if (top === href) return;                       // no-op on repeat (rotation, re-render, StrictMode double-fire)
  // Unified pathname-based resolution.
  //
  // navigate() / setSearchParams() calls that land on a pathname already
  // present in the stack must NEVER push a new entry — regardless of
  // whether search or hash strings differ.
  //
  //   • Same pathname as top             → replace top in place (in-page
  //                                        filter / tab / `just_created`
  //                                        cleanup / rotation-triggered
  //                                        param normalisation).
  //   • Same pathname deeper in stack    → unwind to that index and
  //                                        replace with the new href so
  //                                        the fresh search/hash is
  //                                        preserved without duplicating
  //                                        (real backward navigation
  //                                        with fresh query params).
  //   • Pathname absent from stack       → push (true forward nav).
  //
  // Search / hash are stripped only for comparison; the full href
  // (with query + hash) is what gets stored so downstream `resolveBackHref`
  // consumers can restore filter state on return.
  const newPath = href.split("?")[0].split("#")[0];
  for (let i = state.stack.length - 1; i >= 0; i--) {
    const entry = state.stack[i];
    const entryPath = entry.split("?")[0].split("#")[0];
    if (entryPath === newPath) {
      state.stack.length = i + 1;
      state.stack[i] = href;
      persist();
      return;
    }
  }
  state.stack.push(href);
  if (state.stack.length > MAX_STACK) {
    state.stack.splice(0, state.stack.length - MAX_STACK);
  }
  persist();
}

export function getPreviousUrl() {
  return state.stack.length >= 2 ? state.stack[state.stack.length - 2] : null;
}

/**
 * Resolve the best "Back" destination for a page.
 *
 * Priority:
 *   1. `location.state.from` — explicit origin passed by the calling link.
 *   2. The tracker's previous URL (if non-empty and not the current page).
 *   3. The caller-supplied fallback (e.g. "/items" or "/collections").
 *
 * Always returns a string path.
 */
export function resolveBackHref({ stateFrom, currentPath, fallback }) {
  if (typeof stateFrom === "string" && stateFrom && stateFrom !== currentPath) {
    return stateFrom;
  }
  const prev = getPreviousUrl();
  if (prev && prev !== currentPath) return prev;
  return fallback;
}
